import * as React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import type { R3FixtureQuestion } from '@reos/duediligence';
import { ANSWER_SOURCE_CAPTIONS, ANSWER_QUESTION_TEXTS } from '../lib/texts.js';
import { ANSWER_SOURCE_KINDS, canAnswerSourceCloseGate, isDeclarativeAnswerSource } from '../lib/answer-source.js';
import type { AnswerSourceKind } from '@reos/duediligence';
import {
  submitCaseAnswer,
  type AnswerAccessPassageKind,
  type AnswerDemolitionRoofKind,
} from '../lib/answers-api.js';
import { DateField } from './date-field.js';
import { CzechNumberInput } from './czech-number-input.js';
import { humanQuestionText } from '../lib/case-captions.js';

export type AnswerTarget =
  | { readonly kind: 'question'; readonly question: R3FixtureQuestion }
  | { readonly kind: 'gate'; readonly gateId: string; readonly needs: readonly string[] };

const DEMO_ROOF_QUESTION_PREFIX = 'Q-DEMO-ROOF-';
const DEMO_HEIGHT_QUESTION_PREFIX = 'Q-DEMO-HEIGHT-';
const ACCESS_OBSTACLE_QUESTION_PREFIX = 'Q-ACCESS-OBSTACLE-';

type QuestionShape = 'ROOF' | 'HEIGHT' | 'PASSAGE' | 'PLAIN';

function questionShape(questionKey: string): QuestionShape {
  if (questionKey.startsWith(DEMO_ROOF_QUESTION_PREFIX)) return 'ROOF';
  if (questionKey.startsWith(DEMO_HEIGHT_QUESTION_PREFIX)) return 'HEIGHT';
  if (questionKey.startsWith(ACCESS_OBSTACLE_QUESTION_PREFIX)) return 'PASSAGE';
  return 'PLAIN';
}

type SaveStatus = { kind: 'idle' } | { kind: 'saving' } | { kind: 'error'; message: string };

function todayIsoDate(): string {
  return new Date(Date.now()).toISOString().slice(0, 10);
}

/**
 * Panel „Doplnit odpoved" (krok U1) — otevira se nad otevrenou otazkou
 * (Dalsi nejlepsi kroky, Co nevime). Uklada se VZDY pres
 * `POST /v1/duediligence/cases/:id/answers` (zadny tichy zapis do prohlizece) - bez
 * `VITE_AUDIT_API_URL` panel jen rekne, ze ukladani vyzaduje API, a nic
 * neuklada. Odpoved primo na branu (`target.kind === 'gate'`) API zatim
 * nema cim zapsat — panel to rika rovnou, misto aby predstiral zapis.
 */
export function AnswerQuestionPanel({
  target,
  caseId,
  onClose,
  onSaved,
}: {
  readonly target: AnswerTarget | null;
  readonly caseId: string;
  readonly onClose: () => void;
  readonly onSaved: () => void;
}): React.JSX.Element {
  const [text, setText] = React.useState('');
  const [source, setSource] = React.useState<AnswerSourceKind>('CLIENT_STATEMENT');
  const [answeredAt, setAnsweredAt] = React.useState(todayIsoDate());
  const [roof, setRoof] = React.useState<AnswerDemolitionRoofKind>('TILES');
  const [eaveHeightM, setEaveHeightM] = React.useState('');
  const [ridgeHeightM, setRidgeHeightM] = React.useState('');
  const [passageKind, setPassageKind] = React.useState<AnswerAccessPassageKind>('GATE');
  const [status, setStatus] = React.useState<SaveStatus>({ kind: 'idle' });

  React.useEffect(() => {
    if (target === null) return;
    setText('');
    setSource('CLIENT_STATEMENT');
    setAnsweredAt(todayIsoDate());
    setRoof('TILES');
    setEaveHeightM('');
    setRidgeHeightM('');
    setPassageKind('GATE');
    setStatus({ kind: 'idle' });
  }, [target]);

  const shape = target?.kind === 'question' ? questionShape(target.question.questionKey) : 'PLAIN';

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (target === null || target.kind !== 'question' || text.trim() === '') return;
    const structured =
      shape === 'ROOF'
        ? { roof }
        : shape === 'HEIGHT'
          ? {
              ...(eaveHeightM.trim() !== '' ? { eaveHeightM: Number(eaveHeightM) } : {}),
              ...(ridgeHeightM.trim() !== '' ? { ridgeHeightM: Number(ridgeHeightM) } : {}),
            }
          : shape === 'PASSAGE'
            ? { passageKind }
            : undefined;

    setStatus({ kind: 'saving' });
    submitCaseAnswer(caseId, {
      questionId: target.question.id,
      source,
      text: text.trim(),
      answeredAt: new Date(`${answeredAt}T00:00:00.000Z`).toISOString(),
      ...(structured !== undefined ? { structured } : {}),
    })
      .then(() => {
        setStatus({ kind: 'idle' });
        onSaved();
        onClose();
      })
      .catch((error: unknown) => {
        setStatus({ kind: 'error', message: error instanceof Error ? error.message : String(error) });
      });
  };

  return (
    <Dialog.Root open={target !== null} onOpenChange={(open) => { if (!open) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/30" />
        <Dialog.Content
          aria-label={ANSWER_QUESTION_TEXTS.panelTitle}
          className="fixed top-16 left-1/2 -translate-x-1/2 rounded-lg shadow-xl p-1 w-full max-w-lg"
          style={{ background: 'var(--rh-card)' }}
          data-testid="answer-question-panel"
        >
          <div style={{ padding: 16 }}>
            <Dialog.Title style={{ fontSize: 'var(--rh-size-section)', margin: '0 0 4px' }}>{ANSWER_QUESTION_TEXTS.panelTitle}</Dialog.Title>
            {/* Krok UXP3 (P1-5): v dialogu stal strojovy klic otazky
                (`unit_identity.ownership_type`) — text jde pres tentyz filtr
                jako kokpit. */}
            {target?.kind === 'question' && (
              <p className="text-sm m-0 mb-3" style={{ color: 'var(--rh-text-muted)' }}>{humanQuestionText(target.question)}</p>
            )}
            {target?.kind === 'gate' ? (
              <p className="text-sm" style={{ margin: '0 0 12px', color: 'var(--rh-warning)' }} data-testid="answer-question-gate-unsupported">
                {ANSWER_QUESTION_TEXTS.gateUnsupportedHint}
              </p>
            ) : (
              <form onSubmit={handleSubmit}>
                <label className="text-sm" style={{ display: 'block', marginBottom: 10 }}>
                  <span style={{ display: 'block', marginBottom: 4, fontWeight: 550 }}>{ANSWER_QUESTION_TEXTS.textLabel}</span>
                  <textarea
                    className="rh-input"
                    style={{ width: '100%', minHeight: 80, resize: 'vertical' }}
                    value={text}
                    onChange={(e) => { setText(e.target.value); }}
                    placeholder={ANSWER_QUESTION_TEXTS.textPlaceholder}
                    required
                    data-testid="answer-question-text"
                  />
                </label>
                <label className="text-sm" style={{ display: 'block', marginBottom: 10 }}>
                  <span style={{ display: 'block', marginBottom: 4, fontWeight: 550 }}>{ANSWER_QUESTION_TEXTS.sourceLabel}</span>
                  <select
                    className="rh-input"
                    style={{ width: '100%' }}
                    value={source}
                    onChange={(e) => { setSource(e.target.value as AnswerSourceKind); }}
                    data-testid="answer-question-source"
                  >
                    {ANSWER_SOURCE_KINDS.map((kind) => (
                      <option key={kind} value={kind}>{ANSWER_SOURCE_CAPTIONS[kind]}</option>
                    ))}
                  </select>
                </label>
                <label className="text-sm" style={{ display: 'block', marginBottom: 10 }}>
                  <span style={{ display: 'block', marginBottom: 4, fontWeight: 550 }}>{ANSWER_QUESTION_TEXTS.dateLabel}</span>
                  <DateField
                    className="rh-input"
                    value={answeredAt}
                    onValueChange={(next) => { setAnsweredAt(next); }}
                    required
                    testId="answer-question-date"
                  />
                </label>
                {shape === 'ROOF' && (
                  <label className="text-sm" style={{ display: 'block', marginBottom: 10 }}>
                    <span style={{ display: 'block', marginBottom: 4, fontWeight: 550 }}>{ANSWER_QUESTION_TEXTS.roofLabel}</span>
                    <select
                      className="rh-input"
                      style={{ width: '100%' }}
                      value={roof}
                      onChange={(e) => { setRoof(e.target.value as AnswerDemolitionRoofKind); }}
                      data-testid="answer-question-roof"
                    >
                      <option value="TILES">{ANSWER_QUESTION_TEXTS.roofOptionTiles}</option>
                      <option value="METAL">{ANSWER_QUESTION_TEXTS.roofOptionMetal}</option>
                      <option value="ASBESTOS">{ANSWER_QUESTION_TEXTS.roofOptionAsbestos}</option>
                      <option value="UNKNOWN">{ANSWER_QUESTION_TEXTS.roofOptionUnknown}</option>
                    </select>
                  </label>
                )}
                {shape === 'HEIGHT' && (
                  <>
                    <label className="text-sm" style={{ display: 'block', marginBottom: 10 }}>
                      <span style={{ display: 'block', marginBottom: 4, fontWeight: 550 }}>{ANSWER_QUESTION_TEXTS.eaveHeightLabel}</span>
                      {/*
                        Krok UXD2: vyska je MIRA s desetinami — do nativniho
                        `type="number"` cesky „7,5" prohlizec nepusti a pole
                        zustane prazdne.
                      */}
                      <CzechNumberInput
                        className="rh-input"
                        value={eaveHeightM}
                        onValueChange={setEaveHeightM}
                        testId="answer-question-eave-height"
                        ariaLabel={ANSWER_QUESTION_TEXTS.eaveHeightLabel}
                      />
                    </label>
                    <label className="text-sm" style={{ display: 'block', marginBottom: 10 }}>
                      <span style={{ display: 'block', marginBottom: 4, fontWeight: 550 }}>{ANSWER_QUESTION_TEXTS.ridgeHeightLabel}</span>
                      <CzechNumberInput
                        className="rh-input"
                        value={ridgeHeightM}
                        onValueChange={setRidgeHeightM}
                        testId="answer-question-ridge-height"
                        ariaLabel={ANSWER_QUESTION_TEXTS.ridgeHeightLabel}
                      />
                    </label>
                  </>
                )}
                {shape === 'PASSAGE' && (
                  <label className="text-sm" style={{ display: 'block', marginBottom: 10 }}>
                    <span style={{ display: 'block', marginBottom: 4, fontWeight: 550 }}>{ANSWER_QUESTION_TEXTS.passageKindLabel}</span>
                    <select
                      className="rh-input"
                      style={{ width: '100%' }}
                      value={passageKind}
                      onChange={(e) => { setPassageKind(e.target.value as AnswerAccessPassageKind); }}
                      data-testid="answer-question-passage-kind"
                    >
                      <option value="GATE">{ANSWER_QUESTION_TEXTS.passageKindGate}</option>
                      <option value="WALL">{ANSWER_QUESTION_TEXTS.passageKindWall}</option>
                      <option value="OPEN">{ANSWER_QUESTION_TEXTS.passageKindOpen}</option>
                    </select>
                  </label>
                )}
                <p className="text-sm" style={{ margin: '0 0 10px', color: isDeclarativeAnswerSource(source) ? 'var(--rh-warning)' : 'var(--rh-success)' }}>
                  {canAnswerSourceCloseGate(source) ? ANSWER_QUESTION_TEXTS.closesGateHint : ANSWER_QUESTION_TEXTS.declarativeHint}
                </p>
                {status.kind === 'error' && (
                  <p className="text-sm" style={{ margin: '0 0 10px', color: 'var(--rh-danger)' }} data-testid="answer-question-error">
                    {ANSWER_QUESTION_TEXTS.errorPrefix}{status.message}
                  </p>
                )}
                <div className="flex gap-2">
                  <button
                    type="submit"
                    className="rh-btn rh-btn--primary"
                    disabled={text.trim() === '' || status.kind === 'saving'}
                    data-testid="answer-question-save"
                  >
                    {status.kind === 'saving' ? ANSWER_QUESTION_TEXTS.saving : ANSWER_QUESTION_TEXTS.saveButton}
                  </button>
                  <Dialog.Close asChild>
                    <button type="button" className="rh-btn" data-testid="answer-question-cancel">{ANSWER_QUESTION_TEXTS.cancelButton}</button>
                  </Dialog.Close>
                </div>
              </form>
            )}
            {target?.kind === 'gate' && (
              <Dialog.Close asChild>
                <button type="button" className="rh-btn" data-testid="answer-question-cancel">{ANSWER_QUESTION_TEXTS.closeButton}</button>
              </Dialog.Close>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
